import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs
} from '@mui/material';
import {
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Science as ScienceIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  BarChart as ChartIcon
} from '@mui/icons-material';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ScatterChart, Scatter } from 'recharts';

// Types for statistical validation
interface RuleStatistics {
  ruleId: string;
  ruleName: string;
  category: string;
  statistics: {
    mannWhitneyU: {
      statistic: number;
      pValue: number;
      significance: 'highly_significant' | 'significant' | 'marginally_significant' | 'not_significant';
    };
    cohensD: {
      value: number;
      ci_lower: number;
      ci_upper: number;
      interpretation: 'large' | 'medium' | 'small' | 'negligible';
    };
    aucRoc: {
      value: number;
      ci_lower: number;
      ci_upper: number;
    };
    precision: number;
    recall: number;
    f1Score: number;
    sampleSizes: {
      deposits: number;
      background: number;
    };
  };
}

interface ValidationSummary {
  totalRules: number;
  significantRules: number;
  averageEffectSize: number;
  overallF1Score: number;
  crossValidationScore: number;
  recommendedThreshold: number;
}

interface StatisticalValidationDashboardProps {
  ruleStatistics?: RuleStatistics[];
  validationSummary?: ValidationSummary;
  commodity?: string;
  onRuleSelect?: (ruleId: string) => void;
  onExport?: () => void;
}

// Helper functions
const getEffectSizeColor = (cohensD: number): string => {
  const absD = Math.abs(cohensD);
  if (absD >= 0.8) return 'var(--palantir-status-success)'; // Large effect
  if (absD >= 0.5) return '#FFD93D'; // Medium effect
  if (absD >= 0.2) return 'var(--palantir-blue-600)'; // Small effect
  return '#F85149'; // Negligible effect
};

const getSignificanceIcon = (significance: string) => {
  switch (significance) {
    case 'highly_significant':
      return <CheckCircleIcon sx={{ color: 'var(--palantir-status-success)', fontSize: 16 }} />;
    case 'significant':
      return <CheckCircleIcon sx={{ color: '#00D2FF', fontSize: 16 }} />;
    case 'marginally_significant':
      return <WarningIcon sx={{ color: '#FFD93D', fontSize: 16 }} />;
    default:
      return <CancelIcon sx={{ color: '#F85149', fontSize: 16 }} />;
  }
};

const StatisticalValidationDashboard: React.FC<StatisticalValidationDashboardProps> = ({
  ruleStatistics = [],
  validationSummary,
  commodity = 'gold',
  onRuleSelect,
  onExport
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [sortBy, setSortBy] = useState<'effect_size' | 'p_value' | 'f1_score'>('effect_size');
  const [filterSignificant, setFilterSignificant] = useState(false);

  // Mock data for demonstration if none provided
  const mockRuleStats: RuleStatistics[] = ruleStatistics.length > 0 ? ruleStatistics : [
    {
      ruleId: 'sericite_alt',
      ruleName: 'Sericite Alteration',
      category: 'spectral',
      statistics: {
        mannWhitneyU: { statistic: 2834, pValue: 0.0001, significance: 'highly_significant' },
        cohensD: { value: 0.92, ci_lower: 0.78, ci_upper: 1.06, interpretation: 'large' },
        aucRoc: { value: 0.84, ci_lower: 0.79, ci_upper: 0.89 },
        precision: 0.72,
        recall: 0.68,
        f1Score: 0.70,
        sampleSizes: { deposits: 47, background: 500 }
      }
    },
    {
      ruleId: 'fault_prox',
      ruleName: 'Fault Proximity',
      category: 'structural',
      statistics: {
        mannWhitneyU: { statistic: 3156, pValue: 0.003, significance: 'significant' },
        cohensD: { value: 0.68, ci_lower: 0.52, ci_upper: 0.84, interpretation: 'medium' },
        aucRoc: { value: 0.76, ci_lower: 0.71, ci_upper: 0.81 },
        precision: 0.65,
        recall: 0.74,
        f1Score: 0.69,
        sampleSizes: { deposits: 52, background: 500 }
      }
    },
    {
      ruleId: 'au_anomaly',
      ruleName: 'Gold Geochemical Anomaly',
      category: 'geochemical',
      statistics: {
        mannWhitneyU: { statistic: 2467, pValue: 0.00001, significance: 'highly_significant' },
        cohensD: { value: 1.15, ci_lower: 0.98, ci_upper: 1.32, interpretation: 'large' },
        aucRoc: { value: 0.91, ci_lower: 0.87, ci_upper: 0.95 },
        precision: 0.81,
        recall: 0.76,
        f1Score: 0.78,
        sampleSizes: { deposits: 31, background: 500 }
      }
    },
    {
      ruleId: 'host_rock',
      ruleName: 'Favorable Host Rock',
      category: 'geological',
      statistics: {
        mannWhitneyU: { statistic: 4523, pValue: 0.045, significance: 'marginally_significant' },
        cohensD: { value: 0.31, ci_lower: 0.14, ci_upper: 0.48, interpretation: 'small' },
        aucRoc: { value: 0.62, ci_lower: 0.56, ci_upper: 0.68 },
        precision: 0.58,
        recall: 0.54,
        f1Score: 0.56,
        sampleSizes: { deposits: 28, background: 500 }
      }
    },
    {
      ruleId: 'ndvi_change',
      ruleName: 'Vegetation Change',
      category: 'temporal',
      statistics: {
        mannWhitneyU: { statistic: 5234, pValue: 0.082, significance: 'not_significant' },
        cohensD: { value: 0.18, ci_lower: -0.02, ci_upper: 0.38, interpretation: 'negligible' },
        aucRoc: { value: 0.57, ci_lower: 0.51, ci_upper: 0.63 },
        precision: 0.52,
        recall: 0.48,
        f1Score: 0.50,
        sampleSizes: { deposits: 12, background: 500 }
      }
    }
  ];

  const mockSummary: ValidationSummary = validationSummary || {
    totalRules: 15,
    significantRules: 11,
    averageEffectSize: 0.65,
    overallF1Score: 0.68,
    crossValidationScore: 0.71,
    recommendedThreshold: 0.60
  };

  // Sort and filter rules
  const processedRules = useMemo(() => {
    let rules = [...mockRuleStats];
    
    if (filterSignificant) {
      rules = rules.filter(rule => 
        rule.statistics.mannWhitneyU.significance === 'highly_significant' ||
        rule.statistics.mannWhitneyU.significance === 'significant'
      );
    }
    
    rules.sort((a, b) => {
      switch (sortBy) {
        case 'effect_size':
          return Math.abs(b.statistics.cohensD.value) - Math.abs(a.statistics.cohensD.value);
        case 'p_value':
          return a.statistics.mannWhitneyU.pValue - b.statistics.mannWhitneyU.pValue;
        case 'f1_score':
          return b.statistics.f1Score - a.statistics.f1Score;
        default:
          return 0;
      }
    });
    
    return rules;
  }, [mockRuleStats, sortBy, filterSignificant]);

  // Prepare chart data
  const effectSizeData = processedRules.map(rule => ({
    name: rule.ruleName.substring(0, 15),
    cohensD: rule.statistics.cohensD.value,
    ci_lower: rule.statistics.cohensD.ci_lower,
    ci_upper: rule.statistics.cohensD.ci_upper,
    color: getEffectSizeColor(rule.statistics.cohensD.value)
  }));

  const performanceRadarData = processedRules.map(rule => ({
    rule: rule.ruleName.substring(0, 10),
    precision: rule.statistics.precision * 100,
    recall: rule.statistics.recall * 100,
    f1Score: rule.statistics.f1Score * 100,
    auc: rule.statistics.aucRoc.value * 100
  }));

  const handleExportData = () => {
    const exportData = {
      commodity,
      timestamp: new Date().toISOString(),
      summary: mockSummary,
      rules: processedRules.map(rule => ({
        name: rule.ruleName,
        category: rule.category,
        pValue: rule.statistics.mannWhitneyU.pValue,
        cohensD: rule.statistics.cohensD.value,
        cohensD_CI: [rule.statistics.cohensD.ci_lower, rule.statistics.cohensD.ci_upper],
        aucRoc: rule.statistics.aucRoc.value,
        f1Score: rule.statistics.f1Score,
        sampleSizes: rule.statistics.sampleSizes
      }))
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `statistical_validation_${commodity}_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid #30363D' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{
            color: 'var(--palantir-blue-600)',
            fontFamily: 'monospace',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            STATISTICAL VALIDATION DASHBOARD
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={commodity.toUpperCase()}
              size="small"
              sx={{
                backgroundColor: '#FFD93D',
                color: '#000',
                fontFamily: 'monospace',
                fontSize: '10px'
              }}
            />
            <Tooltip title="Export Validation Data">
              <IconButton size="small" onClick={handleExportData} sx={{ color: 'var(--palantir-text-muted)' }}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Statistical Methods Info">
              <IconButton size="small" sx={{ color: 'var(--palantir-text-muted)' }}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ backgroundColor: '#0D1117', border: '1px solid #30363D' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <AssessmentIcon sx={{ color: '#00D2FF', fontSize: 20 }} />
                  <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '11px', fontFamily: 'monospace' }}>
                    SIGNIFICANT RULES
                  </Typography>
                </Box>
                <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '24px', fontFamily: 'monospace', fontWeight: 'bold' }}>
                  {mockSummary.significantRules}/{mockSummary.totalRules}
                </Typography>
                <Typography sx={{ color: '#00D2FF', fontSize: '10px', fontFamily: 'monospace' }}>
                  {((mockSummary.significantRules / mockSummary.totalRules) * 100).toFixed(0)}% significant
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ backgroundColor: '#0D1117', border: '1px solid #30363D' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TrendingUpIcon sx={{ color: 'var(--palantir-status-success)', fontSize: 20 }} />
                  <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '11px', fontFamily: 'monospace' }}>
                    AVG EFFECT SIZE
                  </Typography>
                </Box>
                <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '24px', fontFamily: 'monospace', fontWeight: 'bold' }}>
                  {mockSummary.averageEffectSize.toFixed(2)}
                </Typography>
                <Typography sx={{ color: 'var(--palantir-status-success)', fontSize: '10px', fontFamily: 'monospace' }}>
                  Medium-Large Effect
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ backgroundColor: '#0D1117', border: '1px solid #30363D' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ChartIcon sx={{ color: '#FFD93D', fontSize: 20 }} />
                  <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '11px', fontFamily: 'monospace' }}>
                    OVERALL F1-SCORE
                  </Typography>
                </Box>
                <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '24px', fontFamily: 'monospace', fontWeight: 'bold' }}>
                  {(mockSummary.overallF1Score * 100).toFixed(0)}%
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={mockSummary.overallF1Score * 100}
                  sx={{
                    mt: 1,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: '#30363D',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#FFD93D'
                    }
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ backgroundColor: '#0D1117', border: '1px solid #30363D' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ScienceIcon sx={{ color: '#B388FF', fontSize: 20 }} />
                  <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '11px', fontFamily: 'monospace' }}>
                    CROSS-VALIDATION
                  </Typography>
                </Box>
                <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '24px', fontFamily: 'monospace', fontWeight: 'bold' }}>
                  {(mockSummary.crossValidationScore * 100).toFixed(0)}%
                </Typography>
                <Typography sx={{ color: '#B388FF', fontSize: '10px', fontFamily: 'monospace' }}>
                  5-fold CV accuracy
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: '1px solid #30363D' }}>
        <Tabs
          value={selectedTab}
          onChange={(_, value) => setSelectedTab(value)}
          sx={{
            '& .MuiTab-root': {
              color: 'var(--palantir-text-muted)',
              fontFamily: 'monospace',
              fontSize: '12px',
              minHeight: 40,
              '&.Mui-selected': {
                color: 'var(--palantir-blue-600)'
              }
            }
          }}
        >
          <Tab label="Effect Sizes" />
          <Tab label="Statistical Tests" />
          <Tab label="Performance Metrics" />
          <Tab label="Validation Details" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {/* Effect Sizes Tab */}
        {selectedTab === 0 && (
          <Box>
            <Typography sx={{
              color: '#00D2FF',
              fontFamily: 'monospace',
              fontSize: '12px',
              mb: 2
            }}>
              COHEN'S D EFFECT SIZES WITH 95% CONFIDENCE INTERVALS
            </Typography>
            
            <Paper sx={{ p: 2, backgroundColor: '#0D1117', mb: 3 }}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={effectSizeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#30363D" />
                  <XAxis dataKey="name" tick={{ fill: 'var(--palantir-text-muted)', fontSize: 10 }} angle={-45} textAnchor="end" />
                  <YAxis tick={{ fill: 'var(--palantir-text-muted)', fontSize: 10 }} />
                  <RechartsTooltip
                    contentStyle={{ backgroundColor: '#161B22', border: '1px solid #30363D' }}
                    labelStyle={{ color: 'var(--palantir-text-primary)' }}
                  />
                  <Bar dataKey="cohensD" name="Cohen's d">
                    {effectSizeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>

            <Grid container spacing={2}>
              {processedRules.slice(0, 4).map((rule) => (
                <Grid size={{ xs: 12, sm: 6 }} key={rule.ruleId}>
                  <Paper sx={{ p: 2, backgroundColor: '#161B22', border: '1px solid #30363D' }}>
                    <Typography sx={{
                      color: 'var(--palantir-text-primary)',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      mb: 1
                    }}>
                      {rule.ruleName}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '10px' }}>Effect Size</Typography>
                        <Typography sx={{
                          color: getEffectSizeColor(rule.statistics.cohensD.value),
                          fontSize: '18px',
                          fontFamily: 'monospace',
                          fontWeight: 'bold'
                        }}>
                          d = {rule.statistics.cohensD.value.toFixed(2)}
                        </Typography>
                        <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '9px', fontFamily: 'monospace' }}>
                          CI: [{rule.statistics.cohensD.ci_lower.toFixed(2)}, {rule.statistics.cohensD.ci_upper.toFixed(2)}]
                        </Typography>
                      </Box>
                      <Chip
                        label={rule.statistics.cohensD.interpretation.toUpperCase()}
                        size="small"
                        sx={{
                          backgroundColor: getEffectSizeColor(rule.statistics.cohensD.value),
                          color: '#000',
                          fontSize: '9px',
                          fontWeight: 'bold'
                        }}
                      />
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Statistical Tests Tab */}
        {selectedTab === 1 && (
          <Box>
            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel sx={{ color: 'var(--palantir-text-muted)' }}>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  sx={{ color: 'var(--palantir-text-primary)' }}
                >
                  <MenuItem value="p_value">P-Value</MenuItem>
                  <MenuItem value="effect_size">Effect Size</MenuItem>
                  <MenuItem value="f1_score">F1 Score</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant={filterSignificant ? "contained" : "outlined"}
                size="small"
                onClick={() => setFilterSignificant(!filterSignificant)}
                sx={{
                  borderColor: '#30363D',
                  color: filterSignificant ? '#000' : 'var(--palantir-text-primary)',
                  backgroundColor: filterSignificant ? '#00D2FF' : 'transparent',
                  '&:hover': {
                    backgroundColor: filterSignificant ? '#00B8E6' : 'rgba(0, 210, 255, 0.1)'
                  }
                }}
              >
                Significant Only
              </Button>
            </Box>

            <TableContainer component={Paper} sx={{ backgroundColor: '#0D1117' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#00D2FF', fontFamily: 'monospace', fontSize: '11px' }}>Rule Name</TableCell>
                    <TableCell sx={{ color: '#00D2FF', fontFamily: 'monospace', fontSize: '11px' }}>Category</TableCell>
                    <TableCell sx={{ color: '#00D2FF', fontFamily: 'monospace', fontSize: '11px' }}>U-Statistic</TableCell>
                    <TableCell sx={{ color: '#00D2FF', fontFamily: 'monospace', fontSize: '11px' }}>P-Value</TableCell>
                    <TableCell sx={{ color: '#00D2FF', fontFamily: 'monospace', fontSize: '11px' }}>Significance</TableCell>
                    <TableCell sx={{ color: '#00D2FF', fontFamily: 'monospace', fontSize: '11px' }}>Sample Sizes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {processedRules.map((rule) => (
                    <TableRow
                      key={rule.ruleId}
                      sx={{
                        '&:hover': { backgroundColor: '#161B22' },
                        cursor: 'pointer'
                      }}
                      onClick={() => onRuleSelect?.(rule.ruleId)}
                    >
                      <TableCell sx={{ color: 'var(--palantir-text-primary)', fontFamily: 'monospace', fontSize: '11px' }}>
                        {rule.ruleName}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={rule.category}
                          size="small"
                          sx={{
                            backgroundColor: '#30363D',
                            color: 'var(--palantir-text-primary)',
                            fontSize: '9px',
                            height: 20
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: 'var(--palantir-text-primary)', fontFamily: 'monospace', fontSize: '11px' }}>
                        {rule.statistics.mannWhitneyU.statistic}
                      </TableCell>
                      <TableCell sx={{ color: 'var(--palantir-text-primary)', fontFamily: 'monospace', fontSize: '11px' }}>
                        {rule.statistics.mannWhitneyU.pValue < 0.001 
                          ? '<0.001' 
                          : rule.statistics.mannWhitneyU.pValue.toFixed(4)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getSignificanceIcon(rule.statistics.mannWhitneyU.significance)}
                          <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '10px' }}>
                            {rule.statistics.mannWhitneyU.significance.replace('_', ' ')}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: 'var(--palantir-text-muted)', fontFamily: 'monospace', fontSize: '10px' }}>
                        D: {rule.statistics.sampleSizes.deposits} / B: {rule.statistics.sampleSizes.background}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Alert severity="info" sx={{ mt: 2, backgroundColor: '#21262D', color: 'var(--palantir-text-primary)' }}>
              <Typography sx={{ fontSize: '11px', fontFamily: 'monospace' }}>
                <strong>Statistical Notes:</strong>
                <br />• P-values from Mann-Whitney U test (non-parametric)
                <br />• Significance levels: p &lt; 0.001 (highly), p &lt; 0.01 (significant), p &lt; 0.05 (marginal)
                <br />• D = deposits sample size, B = background sample size
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Performance Metrics Tab */}
        {selectedTab === 2 && (
          <Box>
            <Typography sx={{
              color: '#00D2FF',
              fontFamily: 'monospace',
              fontSize: '12px',
              mb: 2
            }}>
              RULE PERFORMANCE COMPARISON
            </Typography>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2, backgroundColor: '#0D1117' }}>
                  <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '12px', mb: 2, fontFamily: 'monospace' }}>
                    Multi-Metric Performance Radar
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <RadarChart data={performanceRadarData.slice(0, 5)}>
                      <PolarGrid stroke="#30363D" />
                      <PolarAngleAxis dataKey="rule" tick={{ fill: 'var(--palantir-text-muted)', fontSize: 10 }} />
                      <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: 'var(--palantir-text-muted)', fontSize: 10 }} />
                      <Radar name="Precision" dataKey="precision" stroke="#00D2FF" fill="#00D2FF" fillOpacity={0.3} />
                      <Radar name="Recall" dataKey="recall" stroke="var(--palantir-status-success)" fill="var(--palantir-status-success)" fillOpacity={0.3} />
                      <Radar name="F1-Score" dataKey="f1Score" stroke="#FFD93D" fill="#FFD93D" fillOpacity={0.3} />
                      <Radar name="AUC-ROC" dataKey="auc" stroke="#B388FF" fill="#B388FF" fillOpacity={0.3} />
                    </RadarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2, backgroundColor: '#0D1117' }}>
                  <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '12px', mb: 2, fontFamily: 'monospace' }}>
                    AUC-ROC Rankings
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {processedRules.sort((a, b) => b.statistics.aucRoc.value - a.statistics.aucRoc.value).slice(0, 5).map((rule, index) => (
                      <Box key={rule.ruleId} sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1.5,
                        backgroundColor: '#161B22',
                        borderRadius: '4px',
                        border: '1px solid #30363D'
                      }}>
                        <Typography sx={{
                          color: '#FFD93D',
                          fontFamily: 'monospace',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          minWidth: 30
                        }}>
                          #{index + 1}
                        </Typography>
                        <Box sx={{ flex: 1, mx: 2 }}>
                          <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '11px', fontFamily: 'monospace' }}>
                            {rule.ruleName}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={rule.statistics.aucRoc.value * 100}
                            sx={{
                              mt: 0.5,
                              height: 4,
                              borderRadius: 2,
                              backgroundColor: '#30363D',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: '#B388FF'
                              }
                            }}
                          />
                        </Box>
                        <Typography sx={{
                          color: '#B388FF',
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          {(rule.statistics.aucRoc.value * 100).toFixed(0)}%
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Typography sx={{
                color: '#00D2FF',
                fontFamily: 'monospace',
                fontSize: '12px',
                mb: 2
              }}>
                PERFORMANCE METRICS BREAKDOWN
              </Typography>
              <Grid container spacing={2}>
                {processedRules.slice(0, 6).map((rule) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={rule.ruleId}>
                    <Paper sx={{
                      p: 2,
                      backgroundColor: '#161B22',
                      border: '1px solid #30363D',
                      height: '100%'
                    }}>
                      <Typography sx={{
                        color: 'var(--palantir-text-primary)',
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        mb: 2
                      }}>
                        {rule.ruleName}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '10px' }}>Precision</Typography>
                          <Typography sx={{ color: '#00D2FF', fontSize: '10px', fontFamily: 'monospace' }}>
                            {(rule.statistics.precision * 100).toFixed(0)}%
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '10px' }}>Recall</Typography>
                          <Typography sx={{ color: 'var(--palantir-status-success)', fontSize: '10px', fontFamily: 'monospace' }}>
                            {(rule.statistics.recall * 100).toFixed(0)}%
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '10px' }}>F1-Score</Typography>
                          <Typography sx={{ color: '#FFD93D', fontSize: '10px', fontFamily: 'monospace' }}>
                            {(rule.statistics.f1Score * 100).toFixed(0)}%
                          </Typography>
                        </Box>
                        <Divider sx={{ borderColor: '#30363D', my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '10px', fontWeight: 'bold' }}>AUC-ROC</Typography>
                          <Typography sx={{ color: '#B388FF', fontSize: '10px', fontFamily: 'monospace', fontWeight: 'bold' }}>
                            {(rule.statistics.aucRoc.value * 100).toFixed(0)}%
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        )}

        {/* Validation Details Tab */}
        {selectedTab === 3 && (
          <Box>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2, backgroundColor: '#0D1117' }}>
                  <Typography sx={{
                    color: 'var(--palantir-blue-600)',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    mb: 2
                  }}>
                    CROSS-VALIDATION RESULTS
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography sx={{ color: '#00D2FF', fontSize: '11px', mb: 1 }}>
                        Stratified 5-Fold Cross-Validation
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {[0.69, 0.72, 0.71, 0.73, 0.70].map((score, index) => (
                          <Paper key={index} sx={{
                            flex: 1,
                            p: 1,
                            backgroundColor: '#161B22',
                            border: '1px solid #30363D',
                            textAlign: 'center'
                          }}>
                            <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '9px' }}>
                              Fold {index + 1}
                            </Typography>
                            <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '11px', fontFamily: 'monospace' }}>
                              {(score * 100).toFixed(0)}%
                            </Typography>
                          </Paper>
                        ))}
                      </Box>
                      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '10px' }}>
                          Mean Score
                        </Typography>
                        <Typography sx={{ color: 'var(--palantir-status-success)', fontSize: '11px', fontFamily: 'monospace', fontWeight: 'bold' }}>
                          {(mockSummary.crossValidationScore * 100).toFixed(1)}% ± 1.2%
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ borderColor: '#30363D' }} />

                    <Box>
                      <Typography sx={{ color: '#00D2FF', fontSize: '11px', mb: 1 }}>
                        Validation Strategy
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon sx={{ color: 'var(--palantir-status-success)', fontSize: 14 }} />
                          <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '10px' }}>
                            Country-based stratification
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon sx={{ color: 'var(--palantir-status-success)', fontSize: 14 }} />
                          <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '10px' }}>
                            Temporal data separation
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon sx={{ color: 'var(--palantir-status-success)', fontSize: 14 }} />
                          <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '10px' }}>
                            Class imbalance handling
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2, backgroundColor: '#0D1117' }}>
                  <Typography sx={{
                    color: 'var(--palantir-blue-600)',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    mb: 2
                  }}>
                    STATISTICAL POWER ANALYSIS
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography sx={{ color: '#00D2FF', fontSize: '11px', mb: 1 }}>
                        Sample Size Adequacy
                      </Typography>
                      <Box sx={{ backgroundColor: '#161B22', p: 2, borderRadius: '4px' }}>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 6 }}>
                            <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '10px' }}>Total Deposits</Typography>
                            <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '16px', fontFamily: 'monospace', fontWeight: 'bold' }}>
                              170
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 6 }}>
                            <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '10px' }}>Background Points</Typography>
                            <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '16px', fontFamily: 'monospace', fontWeight: 'bold' }}>
                              2,500
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>

                    <Box>
                      <Typography sx={{ color: '#00D2FF', fontSize: '11px', mb: 1 }}>
                        Statistical Power (1-β)
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '10px' }}>Small effect (d=0.2)</Typography>
                          <Typography sx={{ color: '#F85149', fontSize: '10px', fontFamily: 'monospace' }}>0.42</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '10px' }}>Medium effect (d=0.5)</Typography>
                          <Typography sx={{ color: '#FFD93D', fontSize: '10px', fontFamily: 'monospace' }}>0.85</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '10px' }}>Large effect (d=0.8)</Typography>
                          <Typography sx={{ color: 'var(--palantir-status-success)', fontSize: '10px', fontFamily: 'monospace' }}>0.98</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Alert severity="warning" sx={{ backgroundColor: '#21262D', color: 'var(--palantir-text-primary)' }}>
                  <Typography sx={{ fontSize: '11px', fontFamily: 'monospace' }}>
                    <strong>Recommended Threshold:</strong> {(mockSummary.recommendedThreshold * 100).toFixed(0)}% probability
                    <br />
                    This threshold optimizes the balance between precision and recall based on cross-validation results.
                    Consider adjusting based on your exploration risk tolerance and budget constraints.
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default StatisticalValidationDashboard;